// Real FedEx API Integration Service
class RealFedExService {
  constructor() {
    this.baseURL = 'https://apis-sandbox.fedex.com';
    this.apiKey = process.env.REACT_APP_FEDEX_API_KEY;
    this.accountNumber = process.env.REACT_APP_FEDEX_ACCOUNT;
    this.accessToken = null;
  }

  // Get FedEx access token
  async getAccessToken() {
    if (this.accessToken && this.tokenExpiry > Date.now()) {
      return this.accessToken;
    }

    try {
      const response = await fetch(`${this.baseURL}/oauth/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: new URLSearchParams({
          grant_type: 'client_credentials',
          client_id: process.env.REACT_APP_FEDEX_CLIENT_ID,
          client_secret: process.env.REACT_APP_FEDEX_CLIENT_SECRET
        })
      });

      const data = await response.json();
      this.accessToken = data.access_token;
      this.tokenExpiry = Date.now() + (data.expires_in * 1000);
      
      return this.accessToken;
    } catch (error) {
      console.error('Error getting FedEx access token:', error);
      throw new Error('Failed to authenticate with FedEx');
    }
  }

  // Get shipping rates from FedEx
  async getRates(origin, destination, weight, dimensions = {}) {
    try {
      const token = await this.getAccessToken();
      
      const requestBody = {
        accountNumber: {
          value: this.accountNumber
        },
        rateRequestControlParameters: {
          returnTransitTimes: true,
          servicesRequested: ['PRIORITY_OVERNIGHT', 'STANDARD_OVERNIGHT', 'FEDEX_GROUND']
        },
        requestedShipment: {
          shipper: {
            address: {
              streetLines: [origin.street],
              city: origin.city,
              stateOrProvinceCode: origin.state,
              postalCode: origin.zipCode,
              countryCode: origin.country || 'MX'
            }
          },
          recipient: {
            address: {
              streetLines: [destination.street],
              city: destination.city,
              stateOrProvinceCode: destination.state,
              postalCode: destination.zipCode,
              countryCode: destination.country || 'MX'
            }
          },
          requestedPackageLineItems: [{
            weight: {
              units: 'LB',
              value: weight
            },
            dimensions: {
              length: dimensions.length || 10,
              width: dimensions.width || 10,
              height: dimensions.height || 10,
              units: 'IN'
            }
          }],
          rateRequestType: ['LIST', 'ACCOUNT'],
          packageCount: '1'
        }
      };

      const response = await fetch(`${this.baseURL}/ship/v1/rates/quotes`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-locale': 'es_MX'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`FedEx API error: ${response.status}`);
      }

      const data = await response.json();
      return this.parseFedExRates(data);
    } catch (error) {
      console.error('Error getting FedEx rates:', error);
      // Fallback to demo rates if API fails
      return this.getDemoRates(origin, destination, weight);
    }
  }

  // Parse FedEx rates response
  parseFedExRates(data) {
    if (!data.output || !data.output.rateReplyDetails) {
      return [];
    }

    return data.output.rateReplyDetails.map(rate => ({
      carrier: 'FedEx',
      service: rate.serviceName,
      serviceCode: rate.serviceType,
      cost: parseFloat(rate.ratedShipmentDetails[0].totalNetCharge),
      currency: 'USD',
      deliveryTime: this.getDeliveryTime(rate.serviceType),
      guaranteed: rate.serviceType === 'PRIORITY_OVERNIGHT',
      transitDays: rate.commitDetails?.serviceDays || 1
    }));
  }

  // Get delivery time based on service type
  getDeliveryTime(serviceType) {
    const deliveryTimes = {
      'PRIORITY_OVERNIGHT': '1 día',
      'STANDARD_OVERNIGHT': '1-2 días',
      'FEDEX_GROUND': '3-5 días'
    };
    return deliveryTimes[serviceType] || '2-3 días';
  }

  // Create shipment with FedEx
  async createShipment(shipmentData) {
    try {
      const token = await this.getAccessToken();
      
      const requestBody = {
        accountNumber: {
          value: this.accountNumber
        },
        requestedShipment: {
          shipper: {
            contact: {
              personName: shipmentData.origin.name,
              phoneNumber: shipmentData.origin.phone,
              emailAddress: shipmentData.origin.email
            },
            address: {
              streetLines: [shipmentData.origin.address],
              city: shipmentData.origin.city,
              stateOrProvinceCode: shipmentData.origin.state,
              postalCode: shipmentData.origin.zipCode,
              countryCode: shipmentData.origin.country || 'MX'
            }
          },
          recipient: {
            contact: {
              personName: shipmentData.destination.name,
              phoneNumber: shipmentData.destination.phone,
              emailAddress: shipmentData.destination.email
            },
            address: {
              streetLines: [shipmentData.destination.address],
              city: shipmentData.destination.city,
              stateOrProvinceCode: shipmentData.destination.state,
              postalCode: shipmentData.destination.zipCode,
              countryCode: shipmentData.destination.country || 'MX'
            }
          },
          requestedPackageLineItems: [{
            weight: {
              units: 'LB',
              value: shipmentData.package.weight
            },
            dimensions: {
              length: shipmentData.package.length,
              width: shipmentData.package.width,
              height: shipmentData.package.height,
              units: 'IN'
            },
            groupPackageCount: 1
          }],
          shippingDocumentSpecification: {
            shippingDocumentTypes: ['COMMERCIAL_INVOICE'],
            commercialInvoiceDetail: {
              documentFormat: {
                docType: 'PDF',
                stockType: 'PAPER_LETTER'
              }
            }
          },
          labelSpecification: {
            labelFormatType: 'COMMON2D',
            imageType: 'PDF',
            labelStockType: 'PAPER_4X6.75_THERMAL'
          },
          rateRequestType: ['LIST', 'ACCOUNT'],
          requestedPackageLineItems: [{
            weight: {
              units: 'LB',
              value: shipmentData.package.weight
            }
          }]
        }
      };

      const response = await fetch(`${this.baseURL}/ship/v1/shipments`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-locale': 'es_MX'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`FedEx shipment creation error: ${response.status}`);
      }

      const data = await response.json();
      return this.parseFedExShipment(data);
    } catch (error) {
      console.error('Error creating FedEx shipment:', error);
      throw error;
    }
  }

  // Parse FedEx shipment response
  parseFedExShipment(data) {
    const shipment = data.output;
    return {
      trackingNumber: shipment.trackingNumber,
      labelUrl: shipment.labelUrl,
      cost: parseFloat(shipment.rateReplyDetails[0].ratedShipmentDetails[0].totalNetCharge),
      service: shipment.serviceName,
      estimatedDelivery: shipment.estimatedDelivery,
      labelData: shipment.labelData
    };
  }

  // Track shipment with FedEx
  async trackShipment(trackingNumber) {
    try {
      const token = await this.getAccessToken();
      
      const response = await fetch(`${this.baseURL}/track/v1/trackingnumbers`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          includeDetailedScans: true,
          trackingInfo: [{
            trackingNumberInfo: {
              trackingNumber: trackingNumber
            }
          }]
        })
      });

      if (!response.ok) {
        throw new Error(`FedEx tracking error: ${response.status}`);
      }

      const data = await response.json();
      return this.parseFedExTracking(data);
    } catch (error) {
      console.error('Error tracking FedEx shipment:', error);
      throw error;
    }
  }

  // Parse FedEx tracking response
  parseFedExTracking(data) {
    const tracking = data.output.completeTrackResults[0];
    return {
      trackingNumber: tracking.trackingNumber,
      status: tracking.latestStatusDetail.description,
      events: tracking.scanEvents.map(event => ({
        status: event.eventDescription,
        location: event.scanLocation?.city || 'Unknown',
        timestamp: event.date,
        description: event.eventDescription
      })),
      estimatedDelivery: tracking.estimatedDelivery,
      actualDelivery: tracking.actualDelivery
    };
  }

  // Demo rates fallback
  getDemoRates(origin, destination, weight) {
    return [
      {
        carrier: 'FedEx',
        service: 'Priority Overnight',
        serviceCode: 'PRIORITY_OVERNIGHT',
        cost: 150.00,
        currency: 'USD',
        deliveryTime: '1 día',
        guaranteed: true,
        transitDays: 1
      },
      {
        carrier: 'FedEx',
        service: 'Standard Overnight',
        serviceCode: 'STANDARD_OVERNIGHT',
        cost: 120.00,
        currency: 'USD',
        deliveryTime: '1-2 días',
        guaranteed: false,
        transitDays: 2
      },
      {
        carrier: 'FedEx',
        service: 'Ground',
        serviceCode: 'FEDEX_GROUND',
        cost: 85.00,
        currency: 'USD',
        deliveryTime: '3-5 días',
        guaranteed: false,
        transitDays: 4
      }
    ];
  }
}

// Create singleton instance
const realFedExService = new RealFedExService();

export default realFedExService; 